import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Loader, Tabs, Text, useTheme } from '@Sage/hoi-poi-ui';
import { SectionList } from '@Sage/ssm-web5';

import { getCampaignEvents } from 'services/MailingService';
import { getLiteral } from 'utils/getLiteral';

import { getColumns, getComponents } from './campaignRecipientsEventTableConfig';
import { useCampaignRecipientsCounts } from './useCampaignRecipientsCounts';

const PAGE_SIZE = 10;

const CampaignRecipientsPanel = memo(({ campaignId, isActive }) => {
    const theme = useTheme();

    const {
        eventTypes,
        activeType,
        setActiveType,
        countsByType,
        loadingTypes,
        loadingCounts,
        error,
    } = useCampaignRecipientsCounts({ campaignId, isActive });

    const handleEventTabChange = useCallback(
        (key) => {
            setActiveType(key);
        },
        [setActiveType],
    );

    const resolvedActiveType = useMemo(
        () => activeType ?? eventTypes[0]?.type ?? null,
        [activeType, eventTypes],
    );

    const tableColumns = useMemo(
        () => (resolvedActiveType != null ? getColumns(resolvedActiveType) : []),
        [resolvedActiveType],
    );
    const tableComponents = useMemo(() => getComponents(), []);
    const hasActiveCount = useMemo(
        () => resolvedActiveType != null && countsByType[resolvedActiveType] !== undefined,
        [resolvedActiveType, countsByType],
    );

    const initRecipientsTable = useCallback(
        (pageSize, startRow) => {
            if (!campaignId || resolvedActiveType == null) {
                return Promise.resolve({ entities: [], lastRow: 0, total: 0 });
            }

            const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : PAGE_SIZE;
            const offset = Number.isFinite(startRow) && startRow >= 0 ? startRow : 0;

            return getCampaignEvents(campaignId, {
                type: resolvedActiveType,
                offset,
                count: safePageSize,
            }).then((list) => {
                const entities = Array.isArray(list) ? list : [];
                const knownTotal = Number(countsByType[resolvedActiveType]);
                const hasKnownTotal = Number.isFinite(knownTotal) && knownTotal >= 0;
                const fallbackTotal = Math.max(offset + entities.length, 0);
                let lastRow = null;

                // Defensive guard: some APIs can return an empty page at the end before
                // matching the reported total. If we don't close here, DataGrid can treat
                // that extra request as "empty dataset" and hide previously loaded rows.
                if (entities.length === 0) {
                    lastRow = offset;
                } else if (hasKnownTotal) {
                    lastRow = offset + entities.length >= knownTotal ? knownTotal : null;
                } else if (entities.length < safePageSize) {
                    lastRow = offset + entities.length;
                }

                return {
                    entities,
                    lastRow,
                    total: hasKnownTotal ? knownTotal : fallbackTotal,
                };
            });
        },
        [campaignId, resolvedActiveType, countsByType],
    );

    const tableContent = useMemo(
        () => (
            <div className="fm-campaign-details__recipients-table">
                {!hasActiveCount ? (
                    <div className="fm-campaign-details__recipients-loading">
                        <Loader size="big" />
                    </div>
                ) : (
                    <SectionList
                        id={`campaign-recipients-${resolvedActiveType ?? 'default'}`}
                        key={`campaign-recipients-${campaignId}-${resolvedActiveType ?? 'default'}`}
                        className="campaign-recipients-section-list"
                        columns={tableColumns}
                        components={tableComponents}
                        initTable={initRecipientsTable}
                        pageSize={PAGE_SIZE}
                        rowHeight={40}
                        domLayout="normal"
                        useLazyLoad
                        initOnReady
                        infiniteScrolling
                        useLoaderPlaceholder
                    />
                )}
            </div>
        ),
        [
            hasActiveCount,
            tableColumns,
            tableComponents,
            campaignId,
            resolvedActiveType,
            initRecipientsTable,
        ],
    );

    const eventTabs = useMemo(
        () =>
            eventTypes.map((type) => {
                const typeTotal = countsByType[type.type];
                const title = type.name ? getLiteral(type.name) : type.type;
                const countLabel =
                    typeTotal !== undefined ? ` (${typeTotal.toLocaleString()})` : '';

                return {
                    key: type.type,
                    title: `${title}${countLabel}`,
                    content: <></>,
                };
            }),
        [eventTypes, countsByType],
    );

    if (!isActive) {
        return null;
    }

    if (loadingTypes && loadingCounts) {
        return (
            <div className="fm-campaign-details__recipients-loading">
                <Loader size="big" />
            </div>
        );
    }

    return (
        <div className="fm-campaign-details__recipients">
            {error ? <Text color={theme.colors.semantic.negative500}>{error}</Text> : null}

            <>
                <Tabs
                    className="fm-campaign-details__event-tabs"
                    activeKey={resolvedActiveType}
                    onChange={handleEventTabChange}
                    tabs={eventTabs}
                    editable={false}
                />
                <div className="fm-campaign-details__event-tab-content">{tableContent}</div>
            </>
        </div>
    );
});

CampaignRecipientsPanel.displayName = 'CampaignRecipientsPanel';

CampaignRecipientsPanel.propTypes = {
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isActive: PropTypes.bool,
};

export default CampaignRecipientsPanel;
